import { Patient, SiteECApprovalStatus } from '@csp/csp-common-model';
import { PatientInfo } from '../../patient/model/PatientInfo';
import { PermissionType } from '../type/PermissionType';
import { PermissionService } from './PermissionService';

const canManage = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_PATIENT, patient) &&
  !!patient.site?.canEditPatients &&
  !patient.isReadonly;

const canManageMedication = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_MEDICATION, patient) &&
  !!patient.site?.canEditPatients &&
  !patient.isReadonly;

const canBeInvited = (patient: Patient): boolean => {
  const canManagePatient = PermissionService.hasPatientPermission(PermissionType.MANAGE_PATIENT, patient);
  const isECAApprovedSite = patient.site?.ecApprovalStatus?.status === SiteECApprovalStatus.APPROVED;
  const canEnrollPatients = patient.site?.canEnrollPatients;
  return (
    (canManagePatient && !patient.isReadonly && patient.hasNoAccount && isECAApprovedSite && canEnrollPatients) ?? false
  );
};

const canManageAnonymous = (patient: Patient): boolean => {
  const canManagePatient = PermissionService.hasPatientPermission(PermissionType.MANAGE_PATIENT, patient);
  return canManagePatient && !patient.isReadonly && patient.isAnonymous;
};

const canViewContactPoint = (patient: PatientInfo): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_PATIENT_CONTACT_POINT, patient) && !patient.hideEmail;

const canViewAccount = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_PATIENT_ACCOUNT, patient);

const canManageVisit = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_VISIT, patient) &&
  !!patient.site?.canBookVisits &&
  !patient.isReadonly;

const canViewHealthEvent = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_HEALTH_EVENT_RESPONSE, patient);

const canManageHealthEvent = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_HEALTH_EVENT, patient);

const canViewQuestionnaires = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_QUESTIONNAIRE_RESPONSE, patient);

const canManageQuestionnaires = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_QUESTIONNAIRE_RESPONSE, patient);

const canViewInfo = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.READ_PATIENT, patient);

const canManageInfo = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_PATIENT, patient);

const canStartSpirometry = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.CAN_START_SPIROMETRY_SESSION, patient);

const canManageSpirometry = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.CAN_MANAGE_SPIROMETRY_SESSION, patient);

const canInitiateOnDemandMeasurementRequest = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.CAN_INITIATE_ON_DEMAND_MEASUREMENT_REQUEST, patient);

const canViewDataChangeEdit = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_DATA_CHANGE_EDIT, patient);

const canManageDataChangeEdit = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_DATA_CHANGE_EDIT, patient);

const canManageHbs = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_HBS, patient);

const canManageRpmEvent = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_RPM_EVENT, patient);

const canManageRequestTags = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.MANAGE_REQUEST_TAGS, patient);

const canViewRTSMChip = (patient: Patient): boolean =>
  PermissionService.hasPatientPermission(PermissionType.VIEW_RTSM_CHIP, patient);

export const PatientPermissionService = {
  canManage,
  canManageMedication,
  canBeInvited,
  canManageAnonymous,
  canViewContactPoint,
  canViewAccount,
  canManageVisit,
  canViewHealthEvent,
  canViewRTSMChip,
  canManageHealthEvent,
  canViewQuestionnaires,
  canManageQuestionnaires,
  canViewInfo,
  canManageInfo,
  canStartSpirometry,
  canManageSpirometry,
  canInitiateOnDemandMeasurementRequest,
  canViewDataChangeEdit,
  canManageDataChangeEdit,
  canManageHbs,
  canManageRpmEvent,
  canManageRequestTags,
};
